import { routing } from '@/src/i18n/routing';
import { extractMaloneLotIdEncoded } from '@/src/lib/utils/listingSlugUtils';

const SEARCH_HDP_HISTORY_INTENT_KEY = 'hotpads:search-hdp-history-intent';
const SEARCH_HDP_ACTIVE_ENTRY_KEY = 'hotpads:search-hdp-active-entry';
const SEARCH_HDP_PENDING_SCROLL_RESTORE_KEY = 'hotpads:search-hdp-pending-scroll-restore';
const SEARCH_HDP_NAVIGATION_SCROLL_RESET_KEY = 'hotpads:search-hdp-navigation-scroll-reset';
const SEARCH_HDP_HISTORY_STATE_KEY = '__hotpadsSearchHdpEntry';
const SEARCH_HDP_RESTORE_STATE_KEY = '__hotpadsSearchHdpRestore';
const SEARCH_HDP_HISTORY_INTENT_TTL_MS = 10 * 60 * 1000;
const SEARCH_CONTENT_PANEL_SELECTOR = '[data-search-content-panel]';

type SearchHdpHistoryIntent = {
  hdpPath: string;
  searchPath: string;
  searchScrollY: number;
  historyDepth: number;
  createdAt: number;
};

type SearchHdpScrollRestore = {
  searchPath: string;
  scrollY: number;
  historyDepth: number;
  createdAt: number;
};

type SearchHdpNavigationScrollReset = {
  hdpPath: string;
  createdAt: number;
};

export type SearchHdpBackEntry = {
  searchPath: string;
  scrollY: number;
  historyDepth: number;
};

type MarkSearchHdpHistoryIntentOptions = {
  searchScrollY?: number;
};

function normalizeHistoryDepth(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 1
    ? Math.floor(value)
    : 1;
}

function normalizePath(href: string | null | undefined, options: { preserveQuery?: boolean } = {}): string | null {
  if (!href) return null;

  try {
    const base = typeof window === 'undefined' ? 'https://www.hotpads.com' : window.location.origin;
    const url = new URL(href, base);
    const path = url.pathname.replace(/\/+$/g, '');
    const segments = path.split('/').filter(Boolean);
    const pathWithoutLocale = routing.locales.includes(
      segments[0] as (typeof routing.locales)[number],
    )
      ? segments.slice(1)
      : segments;
    const normalizedPath = pathWithoutLocale.length ? `/${pathWithoutLocale.join('/')}` : '/';

    return options.preserveQuery ? `${normalizedPath}${url.search}${url.hash}` : normalizedPath;
  } catch {
    return null;
  }
}

type HdpPathIdentity = {
  maloneLotIdEncoded: string;
  suffix: 'pad' | 'building';
  unit: string | null;
};

function getHdpPathIdentity(path: string): HdpPathIdentity | null {
  const segments = path.split('/').filter(Boolean);
  const suffix = segments.at(-1);
  if ((suffix !== 'pad' && suffix !== 'building') || segments.length < 2 || segments.length > 3) {
    return null;
  }

  const maloneLotIdEncoded = extractMaloneLotIdEncoded(segments[0]);
  if (!maloneLotIdEncoded) return null;

  return {
    maloneLotIdEncoded,
    suffix,
    unit: segments.length === 3 ? segments[1] : null,
  };
}

function isCanonicalizedHdpPath(intentPath: string, currentPath: string): boolean {
  const intentIdentity = getHdpPathIdentity(intentPath);
  const currentIdentity = getHdpPathIdentity(currentPath);
  return (
    intentIdentity?.maloneLotIdEncoded === currentIdentity?.maloneLotIdEncoded &&
    intentIdentity?.suffix === currentIdentity?.suffix &&
    intentIdentity?.unit === currentIdentity?.unit
  );
}

function queueSearchHdpNavigationScrollReset(hdpPath: string) {
  try {
    window.sessionStorage.setItem(
      SEARCH_HDP_NAVIGATION_SCROLL_RESET_KEY,
      JSON.stringify({ hdpPath, createdAt: Date.now() } satisfies SearchHdpNavigationScrollReset),
    );
  } catch {
    // Best effort only. App Router's default scroll behavior remains the fallback.
  }
}

function readSearchHdpNavigationScrollReset(now = Date.now()): SearchHdpNavigationScrollReset | null {
  try {
    const raw = window.sessionStorage.getItem(SEARCH_HDP_NAVIGATION_SCROLL_RESET_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<SearchHdpNavigationScrollReset>;
    if (
      typeof parsed.hdpPath !== 'string' ||
      typeof parsed.createdAt !== 'number' ||
      now - parsed.createdAt > SEARCH_HDP_HISTORY_INTENT_TTL_MS
    ) {
      window.sessionStorage.removeItem(SEARCH_HDP_NAVIGATION_SCROLL_RESET_KEY);
      return null;
    }

    return { hdpPath: parsed.hdpPath, createdAt: parsed.createdAt };
  } catch {
    window.sessionStorage.removeItem(SEARCH_HDP_NAVIGATION_SCROLL_RESET_KEY);
    return null;
  }
}

export function consumeSearchHdpNavigationScrollReset(currentHref?: string) {
  if (typeof window === 'undefined') return false;

  const currentPath = normalizePath(currentHref ?? window.location.href);
  const reset = readSearchHdpNavigationScrollReset();
  if (!currentPath || !reset) return false;

  window.sessionStorage.removeItem(SEARCH_HDP_NAVIGATION_SCROLL_RESET_KEY);
  return reset.hdpPath === currentPath || isCanonicalizedHdpPath(reset.hdpPath, currentPath);
}

function getSearchScrollElement(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector<HTMLElement>(SEARCH_CONTENT_PANEL_SELECTOR);
}

function getCurrentSearchScrollY(): number {
  const element = getSearchScrollElement();
  if (element) {
    return element.scrollTop;
  }

  return window.scrollY || window.pageYOffset || 0;
}

function restoreSearchScrollY(scrollY: number) {
  const element = getSearchScrollElement();
  if (element) {
    element.scrollTop = scrollY;
    window.requestAnimationFrame(() => {
      element.scrollTop = scrollY;
    });
    return;
  }

  window.scrollTo(0, scrollY);
  window.requestAnimationFrame(() => window.scrollTo(0, scrollY));
}

function readIntent(now = Date.now()): SearchHdpHistoryIntent | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(SEARCH_HDP_HISTORY_INTENT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<SearchHdpHistoryIntent>;
    if (
      typeof parsed.hdpPath !== 'string' ||
      typeof parsed.searchPath !== 'string' ||
      typeof parsed.searchScrollY !== 'number' ||
      typeof parsed.createdAt !== 'number'
    ) {
      window.sessionStorage.removeItem(SEARCH_HDP_HISTORY_INTENT_KEY);
      return null;
    }

    if (now - parsed.createdAt > SEARCH_HDP_HISTORY_INTENT_TTL_MS) {
      window.sessionStorage.removeItem(SEARCH_HDP_HISTORY_INTENT_KEY);
      return null;
    }

    return {
      hdpPath: parsed.hdpPath,
      searchPath: parsed.searchPath,
      searchScrollY: parsed.searchScrollY,
      historyDepth: normalizeHistoryDepth(parsed.historyDepth),
      createdAt: parsed.createdAt,
    };
  } catch {
    window.sessionStorage.removeItem(SEARCH_HDP_HISTORY_INTENT_KEY);
    return null;
  }
}

function readActiveEntry(now = Date.now()): SearchHdpHistoryIntent | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(SEARCH_HDP_ACTIVE_ENTRY_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<SearchHdpHistoryIntent>;
    if (
      typeof parsed.hdpPath !== 'string' ||
      typeof parsed.searchPath !== 'string' ||
      typeof parsed.searchScrollY !== 'number' ||
      typeof parsed.createdAt !== 'number'
    ) {
      window.sessionStorage.removeItem(SEARCH_HDP_ACTIVE_ENTRY_KEY);
      return null;
    }

    if (now - parsed.createdAt > SEARCH_HDP_HISTORY_INTENT_TTL_MS) {
      window.sessionStorage.removeItem(SEARCH_HDP_ACTIVE_ENTRY_KEY);
      return null;
    }

    return {
      hdpPath: parsed.hdpPath,
      searchPath: parsed.searchPath,
      searchScrollY: parsed.searchScrollY,
      historyDepth: normalizeHistoryDepth(parsed.historyDepth),
      createdAt: parsed.createdAt,
    };
  } catch {
    window.sessionStorage.removeItem(SEARCH_HDP_ACTIVE_ENTRY_KEY);
    return null;
  }
}

function writeActiveEntry(entry: SearchHdpHistoryIntent) {
  try {
    window.sessionStorage.setItem(SEARCH_HDP_ACTIVE_ENTRY_KEY, JSON.stringify(entry));
  } catch {
    // Best-effort only. The crawlable HDP back href remains the deterministic fallback.
  }
}

function readPendingScrollRestore(now = Date.now()): SearchHdpScrollRestore | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(SEARCH_HDP_PENDING_SCROLL_RESTORE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<SearchHdpScrollRestore>;
    if (
      typeof parsed.searchPath !== 'string' ||
      typeof parsed.scrollY !== 'number' ||
      typeof parsed.createdAt !== 'number'
    ) {
      window.sessionStorage.removeItem(SEARCH_HDP_PENDING_SCROLL_RESTORE_KEY);
      return null;
    }

    if (now - parsed.createdAt > SEARCH_HDP_HISTORY_INTENT_TTL_MS) {
      window.sessionStorage.removeItem(SEARCH_HDP_PENDING_SCROLL_RESTORE_KEY);
      return null;
    }

    return {
      searchPath: parsed.searchPath,
      scrollY: parsed.scrollY,
      historyDepth: normalizeHistoryDepth(parsed.historyDepth),
      createdAt: parsed.createdAt,
    };
  } catch {
    window.sessionStorage.removeItem(SEARCH_HDP_PENDING_SCROLL_RESTORE_KEY);
    return null;
  }
}

function readHistoryRestore(): SearchHdpScrollRestore | null {
  if (typeof window === 'undefined') return null;

  const historyRestore = window.history.state?.[SEARCH_HDP_RESTORE_STATE_KEY] as
    | Partial<SearchHdpScrollRestore>
    | undefined;
  if (
    window.history.state?.[SEARCH_HDP_HISTORY_STATE_KEY] !== true ||
    typeof historyRestore?.searchPath !== 'string' ||
    typeof historyRestore.scrollY !== 'number'
  ) {
    return null;
  }

  return {
    searchPath: historyRestore.searchPath,
    scrollY: historyRestore.scrollY,
    historyDepth: normalizeHistoryDepth(historyRestore.historyDepth),
    createdAt:
      typeof historyRestore.createdAt === 'number' ? historyRestore.createdAt : Date.now(),
  };
}

export function markSearchHdpHistoryIntent(
  hdpHref: string,
  options: MarkSearchHdpHistoryIntentOptions = {},
) {
  if (typeof window === 'undefined') return;

  const hdpPath = normalizePath(hdpHref);
  const searchPath = normalizePath(window.location.href, { preserveQuery: true });
  const searchScrollY = options.searchScrollY ?? getCurrentSearchScrollY();
  if (!hdpPath || !searchPath) return;

  queueSearchHdpNavigationScrollReset(hdpPath);

  try {
    window.sessionStorage.setItem(
      SEARCH_HDP_HISTORY_INTENT_KEY,
      JSON.stringify({
        hdpPath,
        searchPath,
        searchScrollY,
        historyDepth: 1,
        createdAt: Date.now(),
      } satisfies SearchHdpHistoryIntent),
    );
  } catch {
    // Storage is a best-effort provenance hint. If it is unavailable, the
    // crawlable HDP back href remains the deterministic fallback.
  }
}

export function markSearchHdpContinuationIntent(hdpHref: string) {
  if (typeof window === 'undefined') return false;

  const hdpPath = normalizePath(hdpHref);
  const currentPath = normalizePath(window.location.href);
  if (!hdpPath || !currentPath) return false;

  queueSearchHdpNavigationScrollReset(hdpPath);

  const activeEntry = readActiveEntry();
  const historyRestore = readHistoryRestore();
  const source =
    activeEntry?.hdpPath === currentPath
      ? {
          searchPath: activeEntry.searchPath,
          scrollY: activeEntry.searchScrollY,
          historyDepth: activeEntry.historyDepth,
        }
      : historyRestore
        ? {
            searchPath: historyRestore.searchPath,
            scrollY: historyRestore.scrollY,
            historyDepth: historyRestore.historyDepth,
          }
        : null;

  if (!source) return false;

  try {
    window.sessionStorage.setItem(
      SEARCH_HDP_HISTORY_INTENT_KEY,
      JSON.stringify({
        hdpPath,
        searchPath: source.searchPath,
        searchScrollY: source.scrollY,
        historyDepth: source.historyDepth + 1,
        createdAt: Date.now(),
      } satisfies SearchHdpHistoryIntent),
    );
    return true;
  } catch {
    return false;
  }
}

export function prepareSearchHdpHistoryEntry(currentHref?: string) {
  if (typeof window === 'undefined') return false;

  const currentPath = normalizePath(currentHref ?? window.location.href);
  const activeEntry = readActiveEntry();
  if (currentPath && activeEntry?.hdpPath === currentPath) {
    return true;
  }

  const historyRestore = readHistoryRestore();
  if (currentPath && historyRestore) {
    writeActiveEntry({
      hdpPath: currentPath,
      searchPath: historyRestore.searchPath,
      searchScrollY: historyRestore.scrollY,
      historyDepth: historyRestore.historyDepth,
      createdAt: historyRestore.createdAt,
    });
    return true;
  }

  const intent = readIntent();
  if (!currentPath || !intent) return false;

  if (intent.hdpPath !== currentPath && !isCanonicalizedHdpPath(intent.hdpPath, currentPath)) {
    window.sessionStorage.removeItem(SEARCH_HDP_HISTORY_INTENT_KEY);
    return false;
  }

  // Server-side canonicalization can update only the human-readable listing
  // slug while preserving the trailing Malone id. Retain the SRP provenance
  // for that same listing, but bind the active entry to the canonical URL so
  // subsequent HDP back-navigation checks still match.
  const active = { ...intent, hdpPath: currentPath, createdAt: Date.now() } satisfies SearchHdpHistoryIntent;
  writeActiveEntry(active);
  window.history.replaceState(
    {
      ...window.history.state,
      [SEARCH_HDP_HISTORY_STATE_KEY]: true,
      [SEARCH_HDP_RESTORE_STATE_KEY]: {
        searchPath: active.searchPath,
        scrollY: active.searchScrollY,
        historyDepth: active.historyDepth,
        createdAt: active.createdAt,
      } satisfies SearchHdpScrollRestore,
    },
    '',
    window.location.href,
  );
  window.sessionStorage.removeItem(SEARCH_HDP_HISTORY_INTENT_KEY);
  return true;
}

export function getActiveSearchHdpBackPath(currentHref?: string) {
  return getActiveSearchHdpBackEntry(currentHref)?.searchPath ?? null;
}

export function getActiveSearchHdpBackEntry(currentHref?: string): SearchHdpBackEntry | null {
  if (typeof window === 'undefined') return null;

  const currentPath = normalizePath(currentHref ?? window.location.href);
  if (!currentPath) return null;

  const activeEntry = readActiveEntry();
  if (activeEntry?.hdpPath === currentPath) {
    return {
      searchPath: activeEntry.searchPath,
      scrollY: activeEntry.searchScrollY,
      historyDepth: activeEntry.historyDepth,
    };
  }

  const historyRestore = readHistoryRestore();
  if (historyRestore) {
    return {
      searchPath: historyRestore.searchPath,
      scrollY: historyRestore.scrollY,
      historyDepth: historyRestore.historyDepth,
    };
  }

  return null;
}

export function queueSearchHdpBackScrollRestore() {
  if (typeof window === 'undefined') return;

  const historyRestore = readHistoryRestore();
  const activeEntry = readActiveEntry();
  const restore =
    historyRestore
      ? {
          searchPath: historyRestore.searchPath,
          scrollY: historyRestore.scrollY,
          historyDepth: historyRestore.historyDepth,
          createdAt: historyRestore.createdAt,
        }
      : activeEntry
        ? {
            searchPath: activeEntry.searchPath,
            scrollY: activeEntry.searchScrollY,
            historyDepth: activeEntry.historyDepth,
            createdAt: activeEntry.createdAt,
          }
        : undefined;
  if (
    !restore ||
    typeof restore.searchPath !== 'string' ||
    typeof restore.scrollY !== 'number'
  ) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      SEARCH_HDP_PENDING_SCROLL_RESTORE_KEY,
      JSON.stringify({
        searchPath: restore.searchPath,
        scrollY: restore.scrollY,
        historyDepth: restore.historyDepth,
        createdAt: Date.now(),
      } satisfies SearchHdpScrollRestore),
    );
    window.sessionStorage.removeItem(SEARCH_HDP_ACTIVE_ENTRY_KEY);
  } catch {
    // Best effort only. Browser/App Router back still works without this.
  }
}

export function restoreQueuedSearchHdpScroll(currentHref?: string) {
  if (typeof window === 'undefined') return false;

  const currentPath = normalizePath(currentHref ?? window.location.href, { preserveQuery: true });
  const currentPathWithoutQuery = normalizePath(currentHref ?? window.location.href);
  const restore = readPendingScrollRestore();
  if (!currentPath || !restore) return false;

  if (restore.searchPath !== currentPath && restore.searchPath !== currentPathWithoutQuery) return false;

  window.sessionStorage.removeItem(SEARCH_HDP_PENDING_SCROLL_RESTORE_KEY);
  window.sessionStorage.removeItem(SEARCH_HDP_ACTIVE_ENTRY_KEY);
  restoreSearchScrollY(restore.scrollY);
  return true;
}
